import { Box, Chip, Collapse, IconButton, TableCell, TableRow, Typography } from '@mui/material'
import { useState } from 'react'

// others
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'

import { CheckCircle, HighlightOff, Print } from '@mui/icons-material'
import { format } from 'date-fns'
import { Link } from 'react-router-dom'
import { BILL_DELIVERY_CARD_HEADERS, NOT_APPLICABLE } from '../../../../../constants'
import { DeliveryDetailProps } from '../../../../../interfaceTypes/props'
import palette from '../../../../../theme/palette'
import { DeliveryRows } from './DeliveryRows'
import { formatCurrency } from '../../../../../utils'
import { Alert } from '../../../../../components/common/Alert'
import { printBillsV2 } from '../../../../../../services'
import { formatStatus } from '../../../../../utils/billing'

export const DeliveryDetail = ({ delivery, refetch }: DeliveryDetailProps) => {
  const [openRows, setOpenRows] = useState<Array<number | string>>([])

  const amountPaid = delivery.amountPaid || 0
  const writtenOff = delivery.writtenOffPrincipal || 0
  const balance = delivery.amount - amountPaid - writtenOff

  const handlePrintClick = async () => {
    const billId = typeof delivery.billNumber === 'number' ? delivery.billNumber : Number(delivery.billNumber)
    if (!Number.isFinite(billId)) return
    // Printing a client bill marks it as delivered, as on the Bills and bill detail pages. Fund
    // requests are not bills and keep their delivery state.
    const markAsDelivered = delivery.type !== 'FUND'
    try {
      await printBillsV2([billId], { markAsDelivered })
      if (markAsDelivered) refetch?.()
    } catch (e) {
      Alert.error(e instanceof Error ? e.message : 'Failed to generate PDF')
    }
  }

  const handleToggleRow = (rowKey: number | string) => {
    setOpenRows(prevOpenRows =>
      prevOpenRows.includes(rowKey) ? prevOpenRows.filter(key => key !== rowKey) : [...prevOpenRows, rowKey]
    )
  }

  const isOpen = openRows.includes(delivery.billNumber)

  return (
    <>
      <TableRow hover>
        <TableCell
          align="center"
          sx={{
            fontSize: { xs: 13, sm: 15 }
          }}
        >
          <Link
            to={
              delivery.type === 'FUND'
                ? `/billing/fund-request-detail/${delivery.billNumber}`
                : `/billing/bill-detail/${delivery.billNumber}`
            }
            style={{
              color: palette.primary.main,
              textDecoration: 'none'
            }}
            onMouseOver={e => (e.currentTarget.style.textDecoration = 'underline')}
            onMouseOut={e => (e.currentTarget.style.textDecoration = 'none')}
          >
            {delivery.billNumber?.toString() || NOT_APPLICABLE}
          </Link>
        </TableCell>

        <TableCell
          align="center"
          sx={{
            fontSize: { xs: 13, sm: 15 }
          }}
        >
          <Chip
            label={delivery.type === 'FUND' ? 'Fund Request' : 'Client Bill'}
            size="small"
            color={delivery.type === 'FUND' ? 'secondary' : 'default'}
            variant="outlined"
          />
        </TableCell>

        <TableCell
          align="center"
          sx={{
            fontSize: { xs: 13, sm: 15 }
          }}
        >
          {delivery.success ? <CheckCircle color="success" /> : <HighlightOff color="error" />}
        </TableCell>

        <TableCell
          align="center"
          sx={{
            fontSize: { xs: 13, sm: 15 }
          }}
        >
          {delivery.date ? format(delivery.date, 'MM-dd-yyyy') : '-'}
        </TableCell>
        <TableCell
          align="center"
          sx={{
            fontSize: { xs: 13, sm: 15 }
          }}
        >
          {formatCurrency(delivery.amount || 0)}
        </TableCell>
        <TableCell
          align="center"
          sx={{
            fontSize: { xs: 13, sm: 15 }
          }}
        >
          {formatCurrency(amountPaid || 0)}
        </TableCell>
        <TableCell
          align="center"
          sx={{
            fontSize: { xs: 13, sm: 15 }
          }}
        >
          {formatCurrency(writtenOff || 0)}
        </TableCell>
        <TableCell
          align="center"
          sx={{
            fontSize: { xs: 13, sm: 15 }
          }}
        >
          {formatCurrency(balance || 0)}
        </TableCell>
        <TableCell
          align="center"
          sx={{
            fontSize: { xs: 13, sm: 15 }
          }}
        >
          {formatStatus(delivery.status || '')}
        </TableCell>

        <TableCell
          align="center"
          padding="checkbox"
          onClick={() => handleToggleRow(delivery.billNumber)}
          sx={{
            cursor: 'pointer'
          }}
        >
          <ExpandMoreIcon
            sx={{
              transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 0.2s'
            }}
          />
        </TableCell>

        <TableCell align="center">
          <IconButton onClick={handlePrintClick}>
            <Print color="primary" />
          </IconButton>
        </TableCell>
      </TableRow>

      <TableRow>
        <TableCell
          colSpan={BILL_DELIVERY_CARD_HEADERS.length}
          sx={{
            p: 0,
            border: 0,
            background: 'transparent'
          }}
        >
          <Collapse in={isOpen} timeout="auto" unmountOnExit>
            {delivery.clientDeliveries && delivery.clientDeliveries.length > 0 ? (
              delivery.clientDeliveries.map((record, idx) => (
                <DeliveryRows record={record} refetch={refetch} key={idx} delivery={delivery} />
              ))
            ) : (
              <Box p={2}>
                <Typography variant="body2">No delivery records.</Typography>
              </Box>
            )}
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  )
}
